import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Collapse,
  Alert,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { styled } from "@mui/material/styles";
import { TEMPLATE_DELETE_ENDPOINT } from "../../config/urls";
import MessageNode from "./MessageNode";
import { useAuth } from "../../hooks/AuthProvider";
import { getMpmProductCount, getMpmThumbnailImage, getSpmProductName } from "./mpmPreviewUtils";
import ButtonOne from "../../common/normal-button";
import { ColorsConstants } from "../../cssUtils/constants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const WhatsAppDialogV2 = ({
  open,
  onClose,
  templateData,
  templatePreviewData,
  onNext,
  mode = "view",
}) => {
  const { getValidToken } = useAuth();
  const [alertObj, setAlertObj] = useState({
    active: false,
    msg: "",
    type: "",
  });

  const handleAlertClose = () => {
    setAlertObj({
      active: false,
      type: "",
      msg: "",
    });
  };

  const handleClose = () => {
    onClose();
    handleAlertClose();
  };

  const handleDisable = async () => {
    const apiUrl = TEMPLATE_DELETE_ENDPOINT + `${templateData.template_name}/`;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    axios
      .delete(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      })
      .then((response) => {
        console.log("API Response:", response.data);
        setAlertObj({
          active: true,
          type: "success",
          msg: "Template Deleted Successfully",
        });
        setTimeout(() => {
          handleClose();
        }, 1500);
      })
      .catch((error) => {
        console.error("API Error:", error.response?.data);
        setAlertObj({
          active: true,
          type: "error",
          msg: "Failed to Delete Template",
        });
      });
  };

  const handleNext = () => {
    if (onNext) {
      onNext(templateData);
    }
    handleClose();
  };

  useEffect(() => {
    let timeoutId;

    if (alertObj.active) {
      timeoutId = setTimeout(() => {
        handleAlertClose();
      }, 5000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [alertObj]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxWidth: 483,
        },
      }}
    >
      <DialogContent
        dividers
          sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E5E7EB",
          bgcolor: "#F9FAFB",
          overflowY: "auto",
        }}
      >
        <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                         {mode === "view" && templatePreviewData && (
          <>
            {templatePreviewData.marketingOption === "carousel" ? (
              <MessageNode
                templateName={templatePreviewData.templateName}
                language={templatePreviewData.language}
                headerType="none"
                headerText=""
                headerFile={null}
                body=""
                sampleBodyFields={[]}
                footer=""
                mode="view"
                allButtons={[]}
                marketingOption="carousel"
                carouselCards={templatePreviewData.carouselCards}
                carouselMainBody={templatePreviewData.carouselMainBody}
                carouselMainBodySampleFields={
                  templatePreviewData.carouselMainBodySampleFields || []
                }
                {...Object.keys(templatePreviewData)
                  .filter((key) => key.startsWith("cardFile_"))
                  .reduce((acc, key) => {
                    acc[key] = templatePreviewData[key];
                    return acc;
                  }, {})}
              />
            ) : (
              <MessageNode
                templateName={
                  templatePreviewData.template_name ||
                  templatePreviewData.templateName
                }
                marketingOption={templatePreviewData.marketingOption || null}
                language={templatePreviewData.language}
                headerType={templatePreviewData.headerType}
                headerText={templatePreviewData.headerText}
                headerFile={templatePreviewData.headerFile}
                body={templatePreviewData.body}
                sampleBodyFields={templatePreviewData.sampleBodyFields}
                sampleHeaderFields={templatePreviewData.sampleHeaderFields}
                footer={templatePreviewData.footer}
                mode="view"
                allButtons={templatePreviewData.allButtons}
                showSampleValues
                location={templatePreviewData.location}
                productCount={getMpmProductCount(templatePreviewData.mpmSections)}
                thumbnailImage={getMpmThumbnailImage(templatePreviewData.mpmThumbnail || templatePreviewData.spmProduct)}
                productName={getSpmProductName(templatePreviewData.spmProduct)}
              />
            )}
          </>
        )}

        {mode === "disable" && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography sx={{ fontSize: "14px", color: "#1D2330", mb: 1 }}>
              Are you sure you want to disable the template:
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: ColorsConstants.primaryPurple100,
              }}
            >
              {templateData?.template_name}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6C7C93", mt: 1 }}>
              (This change is not reversible)
            </Typography>
          </Box>
        )}
                </Box>
   
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: "16px",
          borderTop: "1px solid #DFE5EC",
          justifyContent: "flex-end",
          gap: "12px",
          "& .MuiDialogActions-spacing": {
            gap: "12px",
          },
        }}
      >
        <ButtonOne
          variant="outlined"
          onClick={handleClose}
          
        >
          Cancel
        </ButtonOne>

        {mode === "disable" ? (
          <ButtonOne
            variant="contained"
            onClick={handleDisable}
            sx={{
              backgroundColor: "#DC2626",
              "&:hover": {
                backgroundColor: "#B91C1C",
              },
            }}
          >
            Disable
          </ButtonOne>
        ) : (
          <ButtonOne
            variant="contained"
            onClick={handleNext}
            
          >
            Next
          </ButtonOne>
        )}
      </DialogActions>
      <Collapse in={alertObj.active}>
        <Alert
          severity={alertObj.type}
          onClose={handleAlertClose}
          sx={{ mx: 2, mb: 2, borderRadius: "8px" }}
        >
          {alertObj.msg}
        </Alert>
      </Collapse>
    </BootstrapDialog>
  );
};

export default WhatsAppDialogV2;
